import { Log } from "@fanfanlo";
import { roomData } from "../room/data";
import { IMatrixMessage, ITextMessage, IImageMessage, IFileMessage, IAudioMessage, IVideoMessage, ILocationMessage } from "./types";

const fileLogger = new Log(false, 'im.client.room.messageUtils');
// fileLogger.childrenPaused = true;
// fileLogger.pause = true;

// 类型守卫和消息相关函数
const isTextMessage = (message: IMatrixMessage): message is ITextMessage => 
  message.content.msgtype === 'm.text';

const isImageMessage = (message: IMatrixMessage): message is IImageMessage => 
  message.content.msgtype === 'm.image';

const isFileMessage = (message: IMatrixMessage): message is IFileMessage => 
  message.content.msgtype === 'm.file';

const isAudioMessage = (message: IMatrixMessage): message is IAudioMessage => 
  message.content.msgtype === 'm.audio';

const isVideoMessage = (message: IMatrixMessage): message is IVideoMessage => 
  message.content.msgtype === 'm.video';

const isLocationMessage = (message: IMatrixMessage): message is ILocationMessage => 
  message.content.msgtype === 'm.location';

const isMediaMessage = (message: IMatrixMessage): boolean => 
  ['m.image', 'm.file', 'm.audio', 'm.video'].includes(message.content.msgtype);

const getMessageText = (message: IMatrixMessage): string => {
  if (!message.content) return '';
  if (isTextMessage(message)) {
    return message.content.body;
  }
  if (isMediaMessage(message)) {
    return message.content.body || `[${message.content.msgtype.replace('m.', '').toUpperCase()}]`;
  }
  return JSON.stringify(message.content);
};

const getMessageTime = (message: IMatrixMessage): Date => 
  new Date(message.origin_server_ts);

const getMessageSenderName = (sender: string): string => 
  sender.split(':')[0].substring(1); // 移除 @ 符号和域名部分

function addMessage(roomId: string, message: IMatrixMessage) {
  const logger = fileLogger.sub(false, 'addMessage_fn');
  logger.pause = true;
  logger.log('addMessage', 'roomId=', roomId, 'message=', message);
  
  if (!message.event_id) {
    logger.error('No event_id in message:', message);
    return;
  }
  
  const messageId = message.event_id;
  
  // 只存储我们需要的字段
  const simplifiedMessage = {
    event_id: message.event_id,
    sender: message.sender,
    content: message.content,
    origin_server_ts: message.origin_server_ts,
    type: message.type
  };
  
  // 存储消息到消息映射
  const newMessagesMap = { ...roomData.messagesMap };
  newMessagesMap[messageId] = simplifiedMessage;
  
  // 将消息 ID 添加到房间的消息列表中
  const currentMessages = roomData.messages[roomId] || (roomData.messages[roomId] = []);
  // const newMessages = [...currentMessages];
  const newMessages = currentMessages;
  if (!newMessages.includes(messageId)) {
      newMessages.push(messageId);
  }
  
  logger.log('Message added', 'roomId=', roomId, 'messageId=', messageId, 'message=', simplifiedMessage, 'newMessages=', newMessages);
  logger.log('newMessagesMap=', newMessagesMap);
  
  // 更新数据
  const currentMessagesData = roomData.messages;
  currentMessagesData[roomId] = newMessages;
  
  const currentMessagesMap = roomData.messagesMap;
  currentMessagesMap[messageId] = simplifiedMessage;
}
// 获取房间消息列表
function getMessages(roomId: string) {
  const logger = fileLogger.sub(false, 'getMessages_fn');
  logger.pause = true;
  logger.log('getMessages', 'roomId=', roomId);
  const messageIds = roomData.messages[roomId] || [];
  return messageIds.map(id => roomData.messagesMap[id]);
}

// 获取单条消息
function getMessage(messageId: string) {
  const logger = fileLogger.sub(false, 'getMessage_fn');
  logger.pause = true;
  logger.log('getMessage', 'messageId=', messageId);
  return roomData.messagesMap[messageId];
}

// 清空房间消息
function clearMessages(roomId: string) {
  const logger = fileLogger.sub(false, 'clearMessages_fn');
  logger.pause = true;
  logger.log('clearMessages', 'roomId=', roomId);
  if (roomData.messages[roomId]) {
    // 清除消息 ID 列表，但保留消息映射中的消息对象
    roomData.messages[roomId] = [];
  }
}

// 移除特定消息
function removeMessage(roomId: string, messageId: string) {
  const logger = fileLogger.sub(false, 'removeMessage_fn');
  logger.pause = true;
  logger.log('removeMessage', 'roomId=', roomId, 'messageId=', messageId);
  if (roomData.messages[roomId]) {
    const index = roomData.messages[roomId].indexOf(messageId);
    if (index !== -1) {
      roomData.messages[roomId].splice(index, 1);
    }
  }
  
  // 注意：这里不移除 messagesMap 中的消息，因为其他房间可能还在引用它
  // 如果需要完全移除消息，可以添加一个引用计数或定期清理未引用的消息
}

// 查找策略：实时消息（从后往前）
function findRealtimeInsertIndex(currentMessages: string[], message: IMatrixMessage, messagesMap: any) {
  let insertIndex = currentMessages.length;
  while (insertIndex > 0) {
    const prevMessageId = currentMessages[insertIndex - 1];
    const prevMessage = messagesMap[prevMessageId];
    if (!prevMessage || prevMessage.origin_server_ts <= message.origin_server_ts) {
      break;
    }
    insertIndex--;
  }
  return insertIndex;
}

// 查找策略：历史消息（从前往后）
function findHistoryInsertIndex(currentMessages: string[], message: IMatrixMessage, messagesMap: any) {
  let insertIndex = 0;
  for (; insertIndex < currentMessages.length; insertIndex++) {
    const nextMessageId = currentMessages[insertIndex];
    const nextMessage = messagesMap[nextMessageId];
    if (!nextMessage || nextMessage.origin_server_ts >= message.origin_server_ts) {
      break;
    }
  }
  return insertIndex;
}

// 通用插入逻辑
function insertMessageWithStrategy(
  roomId: string,
  message: IMatrixMessage,
  findInsertIndex: (currentMessages: string[], message: IMatrixMessage, messagesMap: any) => number
) {
  const logger = fileLogger.sub(false, 'insertMessageWithStrategy');
  // logger.pause = true;

  if (!message.event_id) {
    logger.error('No event_id in message:', message);
    return -1;
  }

  const messageId = message.event_id;
  const simplifiedMessage = {
    event_id: message.event_id,
    sender: message.sender,
    content: message.content,
    origin_server_ts: message.origin_server_ts,
    type: message.type
  };

  // 更新消息映射
  roomData.messagesMap[messageId] = simplifiedMessage;

  // 获取当前消息列表
  const currentMessages = roomData.messages[roomId] || (roomData.messages[roomId] = []);

  // 如果消息已存在，先移除
  const existingIndex = currentMessages.indexOf(messageId);
  if (existingIndex !== -1) {
    currentMessages.splice(existingIndex, 1);
  }

  // 查找插入位置
  const insertIndex = findInsertIndex(currentMessages, message, roomData.messagesMap);

  // 插入消息
  currentMessages.splice(insertIndex, 0, messageId);

  // 更新房间消息列表
  // roomData.messages[roomId] = currentMessages;

  logger.log(`Inserted message at index ${insertIndex}`, {
    roomId,
    messageId,
    timestamp: message.origin_server_ts
  });

  return insertIndex;
}

// 重构后的 addRealtimeMessage
function addRealtimeMessage(roomId: string, message: IMatrixMessage) {
  return insertMessageWithStrategy(roomId, message, findRealtimeInsertIndex);
}

// 重构后的 addHistoryMessage
function addHistoryMessage(roomId: string, message: IMatrixMessage) {
  return insertMessageWithStrategy(roomId, message, findHistoryInsertIndex);
}

/**
 * 批量添加历史消息
 * 使用归并排序算法，保持消息按时间升序排列
 */
function batchAddHistoryMessages(roomId: string, messages: IMatrixMessage[]) {
  const logger = fileLogger.sub(false, 'batchAddHistoryMessages_fn');
  logger.pause = true;
  
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }
  
  const startTime = Date.now();
  logger.log('Starting batch add history messages', { roomId, messageCount: messages.length });
  
  // 1. 过滤无效消息并创建消息映射
  const validMessages = messages.filter(msg => {
    if (!msg.event_id) {
      logger.warn('Skipping message with missing event_id', msg);
      return false;
    }
    return true;
  });
  
  // 2. 创建新的消息映射
  const newMessagesMap = { ...roomData.messagesMap };
  const newMessageIds = validMessages.map(msg => {
    const simplified = {
      event_id: msg.event_id,
      sender: msg.sender,
      content: msg.content,
      origin_server_ts: msg.origin_server_ts,
      type: msg.type
    };
    newMessagesMap[msg.event_id] = simplified;
    return { id: msg.event_id, ts: msg.origin_server_ts };
  });
  
  // 3. 获取当前消息列表
  const currentMessages = [...(roomData.messages[roomId] || [])];
  
  // 4. 去重（移除已存在的消息ID）
  const existingIds = new Set(currentMessages);
  const uniqueNewMessages = newMessageIds.filter(({ id }) => {
    const exists = existingIds.has(id);
    if (exists) {
      logger.log('Skipping duplicate message', { messageId: id });
    }
    return !exists;
  });
  
  if (uniqueNewMessages.length === 0) {
    logger.log('No new messages to add after deduplication');
    return [];
  }
  
  // 5. 合并并排序消息
  const mergedMessages = [
    ...currentMessages.map(id => ({
      id,
      ts: roomData.messagesMap[id]?.origin_server_ts || 0
    })),
    ...uniqueNewMessages
  ].sort((a, b) => a.ts - b.ts);
  
  // 6. 更新状态
  // roomData.messagesMap = newMessagesMap;
  Object.assign(roomData.messagesMap, newMessagesMap);
  roomData.messages[roomId] = mergedMessages.map(m => m.id);
  
  const endTime = Date.now();
  logger.log(`Batch added ${uniqueNewMessages.length} messages in ${endTime - startTime}ms`, {
    roomId,
    totalMessages: mergedMessages.length,
    newMessages: uniqueNewMessages.length,
    firstNewMessage: uniqueNewMessages[0],
    lastNewMessage: uniqueNewMessages[uniqueNewMessages.length - 1]
  });
  
  return uniqueNewMessages.map(m => m.id);
}

// 消息相关工具函数
export const messageUtils = {
  addMessage,
  addRealtimeMessage,
  addHistoryMessage,
  batchAddHistoryMessages,
  getMessages,
  getMessage,
  clearMessages,
  removeMessage,
  isTextMessage,
  isImageMessage,
  isFileMessage,
  isAudioMessage,
  isVideoMessage,
  isLocationMessage,
  isMediaMessage,
  getMessageText,
  getMessageTime,
  getMessageSenderName,
};
